/**
 * device/apps/messenger.ts — In-Game Messenger
 *
 * A clean iMessage-style messaging app for the hPhone/hPad. Two views:
 * conversation list (default) and thread view (selected conversation).
 * All rendering is Canvas 2D — no DOM.
 *
 * Sample conversations are baked in for now; a future network layer
 * will replace them with real player-to-player DMs.
 */

import type { GameState } from "../../engine/state.js";
import type { DeviceApp, Rect } from "../app.js";
import { registerApp, drawRoundRect, drawText } from "../app.js";

// ─── Types ───────────────────────────────────────────────────────────────

interface Message {
  from: "player" | "other";
  text: string;
  time: string;
}

interface Conversation {
  contactName: string;
  avatarColor: string;
  lastTimestamp: string;
  unread: number;
  messages: Message[];
}

interface MessengerState {
  view: "list" | "thread";
  selectedIndex: number | null;
  /** Tracks layout rects for hit-testing — rebuilt every frame */
  _rowRects: Array<{ y: number; h: number }>;
  /** Thread view scroll state */
  scrollY: number;
  scrollVelocity: number;
  maxScrollY: number;
}

// ─── Sample Data ─────────────────────────────────────────────────────────

const CONVERSATIONS: Conversation[] = [
  {
    contactName: "Luna",
    avatarColor: "#a78bfa",
    lastTimestamp: "2:14 PM",
    unread: 2,
    messages: [
      { from: "other", text: "Have you seen the new wallpaper at the shop?", time: "2:10 PM" },
      { from: "player", text: "Not yet! Which one?", time: "2:11 PM" },
      { from: "other", text: "The starfield one. It would look amazing in your loft", time: "2:12 PM" },
      { from: "other", text: "I can help you redecorate if you want!", time: "2:14 PM" },
    ],
  },
  {
    contactName: "Kai",
    avatarColor: "#38bdf8",
    lastTimestamp: "1:45 PM",
    unread: 0,
    messages: [
      { from: "player", text: "Want to go fishing at the south pier?", time: "1:30 PM" },
      { from: "other", text: "Sure! I just got a new rod", time: "1:32 PM" },
      { from: "player", text: "Nice, meet you there in 10", time: "1:33 PM" },
      { from: "other", text: "On my way now", time: "1:45 PM" },
    ],
  },
  {
    contactName: "Maple",
    avatarColor: "#fb923c",
    lastTimestamp: "12:05 PM",
    unread: 1,
    messages: [
      { from: "other", text: "Do you have any extra flour?", time: "11:50 AM" },
      { from: "player", text: "I think so, let me check my kitchen", time: "11:52 AM" },
      { from: "player", text: "Yeah I have 3 bags, come grab one", time: "11:55 AM" },
      { from: "other", text: "Tysm!! The mushroom bread recipe needs exactly 1", time: "12:05 PM" },
    ],
  },
  {
    contactName: "Sage",
    avatarColor: "#4ade80",
    lastTimestamp: "Yest.",
    unread: 0,
    messages: [
      { from: "other", text: "Did you finish the lantern quest?", time: "6:20 PM" },
      { from: "player", text: "Almost! I need one more crystal shard", time: "6:22 PM" },
      { from: "other", text: "Check the cave behind the waterfall, I found 3 there", time: "6:24 PM" },
    ],
  },
];

// ─── Layout Constants ────────────────────────────────────────────────────

const HEADER_H = 26;
const ROW_H = 44;
const AVATAR_R = 14;
const BUBBLE_PAD_H = 6;
const BUBBLE_PAD_V = 4;
const BUBBLE_RADIUS = 8;
const BUBBLE_MAX_W_RATIO = 0.7;
const BUBBLE_GAP = 5;
const BUBBLE_FONT = "9px sans-serif";
const BUBBLE_TIME_FONT = "7px sans-serif";
const PLAYER_BUBBLE_COLOR = "#8a5a30";
const OTHER_BUBBLE_COLOR = "#2a2018";
const UNREAD_BADGE_R = 8;
const MSG_SIDE_PAD = 8;

// ─── State Access ────────────────────────────────────────────────────────

function getState(state: GameState): MessengerState {
  if (!state.player.device.appStates["messenger"]) {
    state.player.device.appStates["messenger"] = {
      view: "list",
      selectedIndex: null,
      _rowRects: [],
      scrollY: 0,
      scrollVelocity: 0,
      maxScrollY: 0,
    } satisfies MessengerState;
  }
  return state.player.device.appStates["messenger"] as MessengerState;
}

function totalUnread(): number {
  return CONVERSATIONS.reduce((sum, c) => sum + c.unread, 0);
}

// ─── Drawing Helpers ─────────────────────────────────────────────────────

/** Draw a filled circle with a centered letter */
function drawAvatar(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  color: string,
  letter: string,
): void {
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();

  ctx.font = `bold ${Math.round(r)}px sans-serif`;
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(letter, cx, cy + 1);
}

/** Draw unread badge — small red circle with white number */
function drawBadge(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  count: number,
): void {
  if (count <= 0) return;
  const label = count > 9 ? "9+" : String(count);
  const r = UNREAD_BADGE_R;

  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = "#ef4444";
  ctx.fill();

  ctx.font = "bold 8px sans-serif";
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, x, y + 0.5);
}

/** Draw speech bubble icon for the home screen */
function drawSpeechBubble(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
): void {
  const w = size * 0.52;
  const h = size * 0.38;
  const r = size * 0.12;
  const tailH = size * 0.1;

  ctx.fillStyle = "#ffffff";
  ctx.beginPath();

  // Rounded rect body
  const bx = cx - w / 2;
  const by = cy - h / 2 - tailH * 0.3;
  ctx.moveTo(bx + r, by);
  ctx.lineTo(bx + w - r, by);
  ctx.arcTo(bx + w, by, bx + w, by + r, r);
  ctx.lineTo(bx + w, by + h - r);
  ctx.arcTo(bx + w, by + h, bx + w - r, by + h, r);

  // Tail
  ctx.lineTo(bx + w * 0.4, by + h);
  ctx.lineTo(bx + w * 0.2, by + h + tailH);
  ctx.lineTo(bx + w * 0.25, by + h);

  ctx.lineTo(bx + r, by + h);
  ctx.arcTo(bx, by + h, bx, by + h - r, r);
  ctx.lineTo(bx, by + r);
  ctx.arcTo(bx, by, bx + r, by, r);
  ctx.closePath();
  ctx.fill();

  // Two small dots inside the bubble to suggest text
  const dotR = size * 0.03;
  const dotY = cy - tailH * 0.15;
  ctx.fillStyle = "#22c55e";
  for (let i = -1; i <= 1; i++) {
    ctx.beginPath();
    ctx.arc(cx + i * (size * 0.1), dotY, dotR, 0, Math.PI * 2);
    ctx.fill();
  }
}

// ─── View: Conversation List ─────────────────────────────────────────────

function drawConversationList(
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  ms: MessengerState,
): void {
  const { x, y, w } = rect;

  // Header
  drawText(ctx, "Messages", x + w / 2, y + 6, w, "bold 12px sans-serif", "#f5e6d0", "center");

  // Unread count summary
  const unread = totalUnread();
  if (unread > 0) {
    drawText(
      ctx,
      `${unread} unread`,
      x + w - 8,
      y + 8,
      60,
      "9px sans-serif",
      "#c8a878",
      "right",
    );
  }

  // Reset row rects for hit testing
  ms._rowRects = [];

  // Conversation rows
  for (let i = 0; i < CONVERSATIONS.length; i++) {
    const convo = CONVERSATIONS[i];
    const rowY = y + HEADER_H + i * ROW_H;

    // Store for tap detection
    ms._rowRects.push({ y: HEADER_H + i * ROW_H, h: ROW_H });

    // Subtle separator
    if (i > 0) {
      ctx.fillStyle = "rgba(255,255,255,0.06)";
      ctx.fillRect(x + AVATAR_R * 2 + 16, rowY, w - AVATAR_R * 2 - 24, 0.5);
    }

    // Avatar
    const avatarCx = x + 8 + AVATAR_R;
    const avatarCy = rowY + ROW_H / 2;
    drawAvatar(ctx, avatarCx, avatarCy, AVATAR_R, convo.avatarColor, convo.contactName[0]);

    // Contact name
    const textX = avatarCx + AVATAR_R + 8;
    const nameFont = convo.unread > 0 ? "bold 10px sans-serif" : "10px sans-serif";
    const nameColor = convo.unread > 0 ? "#f5e6d0" : "rgba(245,230,208,0.8)";
    drawText(ctx, convo.contactName, textX, rowY + 8, w * 0.5, nameFont, nameColor, "left");

    // Last message preview
    const lastMsg = convo.messages[convo.messages.length - 1];
    const previewColor = convo.unread > 0 ? "rgba(200,168,120,0.7)" : "rgba(200,168,120,0.4)";
    const maxPreviewW = w - (textX - x) - 50;
    drawText(ctx, lastMsg.text, textX, rowY + 22, maxPreviewW, "9px sans-serif", previewColor, "left");

    // Timestamp (top right)
    drawText(
      ctx,
      convo.lastTimestamp,
      x + w - 8,
      rowY + 8,
      50,
      "8px sans-serif",
      "rgba(255,255,255,0.35)",
      "right",
    );

    // Unread badge (bottom right)
    if (convo.unread > 0) {
      drawBadge(ctx, x + w - 16, rowY + ROW_H / 2 + 4, convo.unread);
    }

    // Chevron hint
    ctx.fillStyle = "rgba(255,255,255,0.15)";
    ctx.font = "10px sans-serif";
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    ctx.fillText("\u203A", x + w - 4, rowY + ROW_H / 2);
  }
}

// ─── View: Thread ────────────────────────────────────────────────────────

function drawThread(
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  ms: MessengerState,
  playerName: string,
  dt: number,
): void {
  if (ms.selectedIndex === null) return;
  const convo = CONVERSATIONS[ms.selectedIndex];
  if (!convo) return;

  const { x, y, w, h } = rect;

  // ── Scroll momentum ─────────────────────────────────────────
  if (Math.abs(ms.scrollVelocity) > 0.5) {
    ms.scrollY += ms.scrollVelocity * dt;
    ms.scrollVelocity *= Math.exp(-6 * dt);
  } else {
    ms.scrollVelocity = 0;
  }

  // ── Header bar with back arrow + contact name ──
  drawRoundRect(ctx, x, y, w, HEADER_H, 0, "rgba(20,14,8,0.88)");

  // Back arrow
  ctx.strokeStyle = "#e8a050";
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(x + 14, y + HEADER_H / 2);
  ctx.lineTo(x + 8, y + HEADER_H / 2);
  ctx.lineTo(x + 12, y + HEADER_H / 2 - 5);
  ctx.moveTo(x + 8, y + HEADER_H / 2);
  ctx.lineTo(x + 12, y + HEADER_H / 2 + 5);
  ctx.stroke();

  // Back label
  drawText(ctx, "Back", x + 18, y + 8, 40, "9px sans-serif", "#e8a050", "left");

  // Contact name centered
  drawText(ctx, convo.contactName, x + w / 2, y + 6, w * 0.5, "bold 11px sans-serif", "#f5e6d0", "center");

  // Small avatar right of center
  drawAvatar(ctx, x + w - 20, y + HEADER_H / 2, 8, convo.avatarColor, convo.contactName[0]);

  // ── Message area (scrollable, clipped) ────────────────────
  const msgAreaY = y + HEADER_H;
  const msgAreaH = h - HEADER_H;

  ctx.save();
  ctx.beginPath();
  ctx.rect(x, msgAreaY, w, msgAreaH);
  ctx.clip();

  // Compute total content height first for maxScrollY
  let totalContentH = 10; // top padding
  const maxBubbleW = w * BUBBLE_MAX_W_RATIO;
  ctx.font = BUBBLE_FONT;

  for (const msg of convo.messages) {
    const textWidth = ctx.measureText(msg.text).width;
    let lines: string[];
    if (textWidth > maxBubbleW - BUBBLE_PAD_H * 2) {
      lines = wrapText(ctx, msg.text, maxBubbleW - BUBBLE_PAD_H * 2);
    } else {
      lines = [msg.text];
    }
    const lineH = 13;
    const totalTextH = lines.length * lineH;
    const actualBubbleH = totalTextH + BUBBLE_PAD_V * 2 + 2;
    totalContentH += actualBubbleH + BUBBLE_GAP + 10;
  }

  ms.maxScrollY = Math.max(0, totalContentH - msgAreaH);
  ms.scrollY = Math.max(0, Math.min(ms.scrollY, ms.maxScrollY));

  // Now draw bubbles with scroll offset
  let cursorY = msgAreaY + 10 - ms.scrollY;
  ctx.font = BUBBLE_FONT;

  for (const msg of convo.messages) {
    const isPlayer = msg.from === "player";
    const textWidth = ctx.measureText(msg.text).width;
    let lines: string[];
    if (textWidth > maxBubbleW - BUBBLE_PAD_H * 2) {
      lines = wrapText(ctx, msg.text, maxBubbleW - BUBBLE_PAD_H * 2);
    } else {
      lines = [msg.text];
    }
    const lineH = 13;
    const totalTextH = lines.length * lineH;
    const actualBubbleH = totalTextH + BUBBLE_PAD_V * 2 + 2;
    const actualBubbleW = lines.length > 1
      ? maxBubbleW
      : Math.min(maxBubbleW, textWidth + BUBBLE_PAD_H * 2 + 4);

    const bubbleX = isPlayer
      ? x + w - MSG_SIDE_PAD - actualBubbleW
      : x + MSG_SIDE_PAD;

    // Skip bubbles above the visible area
    if (cursorY + actualBubbleH + 14 < msgAreaY) {
      cursorY += actualBubbleH + BUBBLE_GAP + 10;
      continue;
    }
    // Stop drawing below the visible area
    if (cursorY > msgAreaY + msgAreaH) break;

    // Bubble background
    const bubbleColor = isPlayer ? PLAYER_BUBBLE_COLOR : OTHER_BUBBLE_COLOR;
    drawRoundRect(ctx, bubbleX, cursorY, actualBubbleW, actualBubbleH, BUBBLE_RADIUS, bubbleColor);

    // Message text
    ctx.font = BUBBLE_FONT;
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    for (let li = 0; li < lines.length; li++) {
      ctx.fillText(lines[li], bubbleX + BUBBLE_PAD_H, cursorY + BUBBLE_PAD_V + li * lineH + 1);
    }

    // Timestamp below bubble
    const timeX = isPlayer ? bubbleX + actualBubbleW : bubbleX;
    const timeAlign: CanvasTextAlign = isPlayer ? "right" : "left";
    drawText(
      ctx,
      msg.time,
      timeX,
      cursorY + actualBubbleH + 1,
      60,
      BUBBLE_TIME_FONT,
      "rgba(255,255,255,0.25)",
      timeAlign,
    );

    cursorY += actualBubbleH + BUBBLE_GAP + 10;
  }

  ctx.restore();
}

/** Simple word-wrap for canvas text */
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let current = "";

  for (const word of words) {
    const test = current ? current + " " + word : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines;
}

// ─── The App ─────────────────────────────────────────────────────────────

const messengerApp: DeviceApp = {
  id: "messenger",
  name: "Messages",
  iconGradient: ["#4a8a50", "#2a6030"],

  drawIcon(ctx, cx, cy, size) {
    drawSpeechBubble(ctx, cx, cy, size);
  },

  drawContent(ctx, rect, state, dt) {
    const ms = getState(state);

    if (ms.view === "thread" && ms.selectedIndex !== null) {
      drawThread(ctx, rect, ms, state.player.name, dt);
    } else {
      drawConversationList(ctx, rect, ms);
    }
  },

  handleTap(localX, localY, state) {
    const ms = getState(state);

    // ── Thread view ──
    if (ms.view === "thread") {
      // Back button area: top-left region
      if (localY < HEADER_H && localX < 60) {
        ms.view = "list";
        ms.selectedIndex = null;
        ms.scrollY = 0;
        ms.scrollVelocity = 0;
        return true;
      }
      return false;
    }

    // ── Conversation list view ──
    for (let i = 0; i < ms._rowRects.length; i++) {
      const row = ms._rowRects[i];
      if (localY >= row.y && localY < row.y + row.h) {
        ms.view = "thread";
        ms.selectedIndex = i;
        ms.scrollY = 0;
        ms.scrollVelocity = 0;
        return true;
      }
    }

    return false;
  },

  handleDrag(dy, state) {
    const ms = getState(state);
    if (ms.view !== "thread") return;
    ms.scrollY = Math.max(0, Math.min(ms.scrollY + dy, ms.maxScrollY));
    ms.scrollVelocity = 0;
  },

  handleDragEnd(velocityY, state) {
    const ms = getState(state);
    if (ms.view !== "thread") return;
    ms.scrollVelocity = velocityY;
  },

  onBack(state) {
    const ms = getState(state);
    if (ms.view === "thread") {
      ms.view = "list";
      ms.selectedIndex = null;
      ms.scrollY = 0;
      ms.scrollVelocity = 0;
      return true;
    }
    return false;
  },

  onOpen(state) {
    const ms = getState(state);
    ms.scrollY = 0;
    ms.scrollVelocity = 0;
  },

  onClose(state) {
    // Return to list view when closing so it's fresh next open
    const ms = getState(state);
    ms.view = "list";
    ms.selectedIndex = null;
    ms.scrollY = 0;
    ms.scrollVelocity = 0;
  },

  badge(_state) {
    return totalUnread();
  },
};

// ─── Register ────────────────────────────────────────────────────────────

registerApp(messengerApp);

export { messengerApp };
